import { supabase } from "@/integrations/supabase/client";
import { capture } from "@/lib/sentry/capture";
import { logger } from "@/utils/logger";
import { cancelBuildingStorageUpload } from "./buildingStorageQuota";

function isDeletionDenied(error: unknown): boolean {
  if (typeof error !== "object" || error === null) return false;
  return ("status" in error && error.status === 403) || ("code" in error && error.code === "42501");
}

/** Best-effort cleanup only after a new object is known to have no document row. */
export async function discardBuildingStorageUpload(
  path: string,
  reservationId: string,
  buildingId: number
): Promise<void> {
  let removal: "removed" | "denied" | "failed" = "failed";
  let cancellation: "requested" | "failed" = "failed";
  try {
    const { data, error } = await supabase.storage.from("documents").remove([path]);
    if (error) throw error;
    if (!data?.some((object) => object.name === path)) {
      throw new Error("Uploaded storage object was not removed");
    }
    removal = "removed";
  } catch (error) {
    if (isDeletionDenied(error)) {
      removal = "denied";
    } else {
      capture(error instanceof Error ? error : new Error(String(error)), {
        tags: { feature: "building-storage-quota", stage: "discard-object" },
        fingerprint: ["building-storage-quota", "discard-object"],
        context: { quota: { buildingId } },
      });
    }
  } finally {
    try {
      await cancelBuildingStorageUpload(reservationId, buildingId);
      cancellation = "requested";
    } catch (error) {
      capture(error instanceof Error ? error : new Error(String(error)), {
        tags: { feature: "building-storage-quota", stage: "cancel-discarded-upload" },
        fingerprint: ["building-storage-quota", "cancel-discarded-upload"],
        context: { quota: { buildingId } },
      });
    }
    void logger.info("api", "building-storage.discard-upload", "Unowned upload cleanup finished", {
      buildingId,
      removal,
      cancellation,
    });
  }
}
